/**
 * 気象バー (ForecastWeatherBar)。design の weather-strip 構造 (design L797 /
 * JS L5343-5386) に準拠: 毎時 24 セル grid の時間帯別セルの帯 + 上部サマリ行
 * (ラベル / 発表時刻 / 最高・最低気温 / 取得元 badge)。
 *
 * - weatherStatus=FETCH_FAILED → 「取得失敗」降格表示 (テキスト併記で a11y 準拠)
 * - weatherStatus=OK かつ weather=undefined → エンプティ表示 (取得失敗と区別)
 * - weatherStatus=OK かつ hourlyWeather あり → 毎時 24 セルの帯 (weather-strip)
 *
 * 気象予報発表時刻 (forecastedAt) / 最高・最低気温 / 時間帯別天気 を表示する
 * (common AC-DFC-008 / AC-DFC-008d)。受信・取込時刻 (receivedAt) はデータ鮮度
 * 監視用の内部データでありここでは表示しない (AC-WD-005b、issue #6934)。
 * 最高・最低気温は proto ForecastWeatherBar から直接読む (demand-forecast-common で
 * 具体化。以前は page-level の temperatureForecastSlots から算出していたが proto
 * field 追加により不要)。
 * 観測地点名 (weather.observationPointName) はここでは表示しない (design
 * demand/forecast-today.html L793-798 準拠で ForecastCard 側の気象観測地点note
 * (page.observationPointName) に一本化済み。weather-strip 内に地点名は無い)。
 *
 * 【毎時 24 セル grid + チャート整列】(weather-icon-unify-48koma 後続 slice、
 * design 2026-07-14 `_dftAlignWeatherStrip` の意図を静的スペーサーで実現):
 * - サーバー配信の hourlyWeather (VPFD は最大 8 件〈hour=0,3,...,21、正時の点が無い
 *   ブロックは含まれない〉、府県・週間の日別は hour=0 の 1 件) を
 *   hour → セル位置へ写像し、該当セルにのみ絵文字を描画する。データの無い
 *   時間セルは「—」(U+2014、design wx-nodata 準拠、title でツールチップ付与。
 *   AC-DFC-012「未到来・欠測とも『—』」で予報行・実績行に共通の記号)。
 * - セル内の気温表示は削除済み (design 2026-07-15 準拠。気温はチャートの
 *   気温ライン/露点で確認できるため)。
 * - 時間セル行は ForecastChart の X 軸コマ (`constants/chartLayout.ts` の
 *   CHART_MARGIN / CHART_LEFT_AXIS_WIDTH / CHART_RIGHT_AXIS_WIDTH と同じ定数)
 *   と横位置整列するため、左右スペーサー + flex-grow 比率 + 絶対配置マークで
 *   構成する。Recharts の X 軸 (category, dataKey=koma) は「先頭コマが plot
 *   左端・末尾コマが plot 右端」の point-scale (band-scale ではない、実測で
 *   確認済み) のため、時刻マーク 24 個 (0..23時) を `position: absolute` +
 *   `left: <hour/23*100>% ` + `translateX(-50%)` でマークの中心そのものを
 *   point 位置に固定する (`justify-content: space-between` はマーク自身の
 *   自然幅の分だけ両端が内側にずれる誤差が実測で残ったため不採用)。時間セル
 *   領域の flex-grow は 46 (= 0 時から 23 時までの point 間隔がコマ 46 個分)、
 *   左の延長領域は前日 23:00〜0:00 の 2、右の延長領域は 23:00〜翌日 3:00 の 8
 *   (チャートは常に前日 23:00〜翌日 3:00 の 57 点、AC-DFD-006a)。
 *   実績行だけ、延長領域に前日 23 時台・翌日 0〜2 時台のマークを同じ point-scale で
 *   置く (AC-DFD-006a-2)。
 *   外枠 Box の border (1px 双方) が ForecastChart の外枠 div (border なし)
 *   より content 幅を 2px 狭めるため、整列行に `marginX="-1px"` で相殺する。
 *   DetailChart / RegionChart (BalanceDemand 系の 48 コマグラフ) の X 軸も
 *   同じ point-scale で、あちらの天気帯は「プロット幅を 48 等分したセルの
 *   左右ガターを半コマぶん削り、セル中央をコマ位置に載せる」ガター方式
 *   (`models/weather/koma.ts` の `weatherStripGutterWidths`、#5087) で整列
 *   させている。本ファイルは同じ静的スペーサーの考え方を踏襲しつつ、毎時
 *   24 セル + 時刻マークの絶対配置という別方式で整列させた (実測で cell 中心と
 *   X 軸 tick 中心の差 <0.1px を確認済み)。design のランタイム JS 計測
 *   (_dftAlignWeatherStrip) は移植しない。
 * - 取得元 badge はチャート整列行 (flex:1 で全幅を占める必要がある) とは別の
 *   上部サマリ行に置く (design は同一行に隣接配置するが、badge を同じ行に
 *   置くと整列行の幅が badge 分だけ縮み、チャートとの横位置整列が崩れるため
 *   上部サマリ行へ寄せた意図的な差異)。
 */
import { timestampDate } from '@bufbuild/protobuf/wkt'
import {
  AuxiliaryDataStatus,
  type ForecastWeatherBar,
  type HourlyWeather,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { useTranslation } from 'react-i18next'

import { Box, Flex, HStack, Text } from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { CompactAlert } from '~/components/common/CompactAlert'
import { HintTooltip } from '~/components/common/HintTooltip'
import { formatJstDateTimeFull } from '~/models/dateTime'
import { decimalToNumber } from '~/models/decimal'
import { telopEmojiOf, telopLabelOf } from '~/models/weather/telop'
import { isDaytimeHour } from '~/models/weather/time'
import {
  CHART_LEFT_AXIS_WIDTH,
  CHART_MARGIN,
  CHART_RIGHT_AXIS_WIDTH,
} from '~/pages/DemandForecastDaily/constants/chartLayout'
import { isDailyWeatherSource } from '~/pages/DemandForecastDaily/models/dailyWeatherSource'
import {
  CHART_SLOT_COUNT,
  DAY_BOUNDARY_PRE_EXTENSION_KOMAS,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import { forecastRowMode } from '~/pages/DemandForecastDaily/models/targetDayWeather'

type Props = {
  weather?: ForecastWeatherBar
  /**
   * チャート上でホバー中の「時」 (0〜23)。 予報・実績どちらの行でも該当セルの
   * 天気アイコンを強調する。 undefined は強調なし
   * (2026-09-18 デザイナー指摘)。
   */
  activeHour?: number
  /**
   * 実績天気 (AMeDAS 観測)。バー本体が描画される時 (下 2 早期 return に
   * 当たらない時) は常に、予測行の下に「実績」行として表示される
   * (AC-DFC-012、D5)。backend の `loadObservedWeather` は当日・過去日のみ
   * 実績を供給するため、翌日以降は undefined または `hourlyWeather` 空で
   * 渡ってくる。その場合も行自体は描画し、未到来・欠測のコマは「—」にする
   * (未到来と欠測を表示上区別しない、AC-DFC-012)。
   */
  actualWeather?: ForecastWeatherBar
  /**
   * 気象データ取得ステータス。FETCH_FAILED の場合は降格表示。
   * undefined / UNSPECIFIED の場合は status 不明扱い (通常表示)。
   */
  weatherStatus?: AuxiliaryDataStatus
  /**
   * 対象日の気温・天気が予測・実績とも 1 件も無いか (AC-DFC-013)。 判定は
   * `models/targetDayWeather.ts` の `isTargetDayWeatherMissing` で、 チャートと
   * 同じ値を受け取る。 true の時は予測行・実績行と最高/最低気温の代わりに
   * 「この日の気象データはありません」を出し、 取得元 badge は残す。
   */
  isTargetDayWeatherMissing?: boolean
  /**
   * 対象日が JST の今日より前 (過去日) か。 true の時は予報のデータが無くても
   * 予報行と最高/最低気温を描き、 無い値を「—」にする (AC-DFC-020。 確定済みでは
   * ない過去日で、 表示時点の予報が無い時)。 予測・実績とも無い時は
   * `isTargetDayWeatherMissing` の案内を優先する (AC-DFC-013)。
   */
  isPastTargetDate?: boolean
  /**
   * 日別粒度で表示するか (AC-WD-008a)。 翌々日 (D+2) 以降は JMA が 3 時間粒度の
   * 天気を発表しておらず、 backend は日別 1 値を hourlyWeather の 1 件で返す
   * (`weather_resolver.go`)。 それをそのまま時間帯別に並べると**実データに無い
   * 粒度**を作ってしまうため、 true の時は 24 時間帯の帯をやめて日別 1 個の
   * アイコンにする (spec:「時間帯別の複数マスに同一アイコンを複製して表示しない」)。
   *
   * 判定 (対象日が JST の翌々日以降か) は呼び出し元が持つ。 AC-WD-005e の
   * 案内文の出し分けと同じ boolean。 なお予報行は、この flag に加えて
   * `weather.weatherSource` が府県・週間 (= 日別 1 値) の時も日別 1 個に
   * する (AC-WD-007a、下記 `isForecastDaily`)。 実績行はこの flag だけで決める。
   */
  isDailyGranularity?: boolean
  /**
   * 前端 (前日 23 時台) の実績天気 (AC-DFD-006a-2)。`hour` は前日の時刻。
   * 時間帯別の実績行だけが延長領域に描く。
   */
  preActualHourlyWeather?: HourlyWeather[]
  /** 後端 (翌日 0〜2 時台) の実績天気 (AC-DFD-006a-2)。`hour` は翌日の時刻。 */
  postActualHourlyWeather?: HourlyWeather[]
  /**
   * ForecastChart の右 YAxis (気温・露点) が表示されているか。表示時は
   * 右スペーサーに右軸幅を加算し、天気帯の右端をチャートの plot 領域右端に
   * 揃える。ForecastCard がチャートと同じ条件 (showTemperature) を配る。既定 false。
   */
  showRightAxis?: boolean
  /**
   * ForecastChart の左 YAxis 幅。動的に広げたい場合に指定する。
   * 未指定時は CHART_LEFT_AXIS_WIDTH を使用 (fix #5432)。
   */
  leftAxisWidth?: number
}

const HOURS_PER_DAY = 24
/** 日別表示で代表に採る時刻 (design `forecast-today.html` の `_daySlots[4]`)。 */
const NOON_HOUR = 12
/**
 * 時刻マーク (0..23時) の先頭〜末尾の point 間隔 (コマ換算)。24 マーク = 23 gap
 * × 2 コマ/gap (= 1 時間) = 46 コマ分。point-scale の推算根拠は本ファイル冒頭
 * JSDoc 参照。
 */
const HOUR_MARK_SPAN_KOMAS = 46

const KOMAS_PER_HOUR = 2

/** 左の延長領域 (前日 23:00〜対象日 0:00 の point 間隔) のコマ数。 */
const LEFT_EXTENSION_KOMAS = DAY_BOUNDARY_PRE_EXTENSION_KOMAS
/** 右の延長領域 (対象日 23:00〜翌日 3:00 の point 間隔) のコマ数。 */
const RIGHT_EXTENSION_KOMAS =
  CHART_SLOT_COUNT -
  DAY_BOUNDARY_PRE_EXTENSION_KOMAS -
  (HOUR_MARK_SPAN_KOMAS + 1)

/** 前端の延長部に入る前日の時台 (23 時台)。 */
const PRE_EXTENSION_HOURS = Array.from(
  { length: LEFT_EXTENSION_KOMAS / KOMAS_PER_HOUR },
  (_, i) => HOURS_PER_DAY - LEFT_EXTENSION_KOMAS / KOMAS_PER_HOUR + i,
)
/**
 * 後端の延長部に入る翌日の時台 (0〜2 時台)。範囲終端 27:00 の点は時台を持たない
 * (翌日 3 時台は範囲外)。
 */
const POST_EXTENSION_HOURS = Array.from(
  { length: (RIGHT_EXTENSION_KOMAS - KOMAS_PER_HOUR) / KOMAS_PER_HOUR },
  (_, i) => i,
)

/**
 * 時刻ラベルを間引く時に残す刻み (3 時間ごと)。 design は狭幅で
 * `@container (max-width: 480px)` により「データなし」 セル (= 3 時間刻み以外) の
 * 時刻ラベルを隠し、 毎時出典では `:nth-child(3n+2)` 以外を隠す
 * (`demand/forecast-today.html`)。 どちらも結果は「3 時間ごとだけ残す」 なので、
 * 出典に依らず hour で判定する。
 */
const HOUR_LABEL_THIN_STEP = 3

/**
 * 時刻ラベルの間引きを発火させるセル領域幅。
 *
 * 24 個のラベルは 23 等分の間隔で並ぶので、 ピッチ = 領域幅 / 23。 2 桁ラベルの
 * 送り幅は Hiragino Kaku Gothic Pro で 1.314em (実測) なので、 10px では 13.14px。
 * ピッチがこれを下回る = 領域幅 < 13.14 x 23 = 302px で隣接ラベルが接触する。
 * 余裕を見て 320px を閾値にする。
 *
 * 間引きが無いと、 グラフ列を CHART_MIN_WIDTH (360px) まで縮めた時に現行の 10px
 * でも数字同士が重なる (デザイナー指摘 2026-08-28 を機に実測)。 design 側は
 * container query で同じことをしているが、 実装には未移植だった。
 */
const HOUR_LABEL_THIN_CSS = {
  '@container (max-width: 320px)': { visibility: 'hidden' },
} as const

/** 天気帯 plot 領域の左スペーサー幅 (チャート左 YAxis 幅 + margin.left)。 */
const LEFT_SPACER_WIDTH = CHART_LEFT_AXIS_WIDTH + CHART_MARGIN.left

type WeatherStripProps = {
  /**
   * 実績行 (AMeDAS 観測) は backend が当日・過去日のみ供給するため、翌日以降は
   * undefined または `hourlyWeather` 空で渡る (D5)。その場合も 24 セル帯自体は
   * 描画し、各セルを「—」(欠測・未到来を区別しない) にする。
   */
  data?: ForecastWeatherBar
  showTime: boolean
  rowLabel: string
  /**
   * チャート上でホバー中の「時」 (0〜23)。 該当セルの天気アイコンを強調する
   * (2026-09-18 デザイナー指摘「グラフ上にホバーした際、 グラフの上の対象の気象
   * アイコンをわかりやすくしたい」)。 undefined は強調なし。
   */
  activeHour?: number
  ariaLabel: string
  /**
   * 延長領域に描く実績天気 (実績行のみ)。undefined の時は延長領域を空にする
   * (予報行)。指定時は各時台を本体と同じくアイコンか「—」で埋める。
   */
  extension?: {
    pre: HourlyWeather[] | undefined
    post: HourlyWeather[] | undefined
  }
  rightSpacerWidth: number
  leftSpacerWidth?: number
  /**
   * データの無いセルの tooltip / title。 undefined の時は付けない (予報のデータが
   * 無い過去日の予報行。 AC-DFC-020 は案内を出さない)。
   */
  noDataTitle?: string
  /**
   * 行ラベルの色。 design は実績行だけ `--text-positive-high` で、 予測行は
   * low-emphasis (`demand/forecast-today.html` の `.wx-label-actual`)。
   * 実装の需給詳細画面 (`BalanceDemandDetail/components/DetailChart`) も
   * 実績 = Text.positiveHigh で揃っており、 本画面だけ取り残されていた。
   */
  labelColor?: string
}

const toByHour = (
  hourly: HourlyWeather[] | undefined,
): Map<number, HourlyWeather> =>
  new Map((hourly ?? []).map((hw) => [hw.hour, hw]))

type WeatherMarkProps = {
  /** マークの時台 (その日の 0〜23 時)。昼夜のアイコン切替に使う。 */
  hour: number
  hw: HourlyWeather | undefined
  isActive?: boolean
  /** データの無いセルの tooltip / title。 undefined の時は付けない (AC-DFC-020)。 */
  noDataTitle?: string
}

/** 1 時台の天気マーク。データがあればアイコン、無ければ「—」(AC-DFC-012)。 */
const WeatherMark = ({
  hour,
  hw,
  isActive = false,
  noDataTitle,
}: WeatherMarkProps): React.ReactElement => {
  if (hw === undefined) return <NoDataMark title={noDataTitle} />
  const emoji = telopEmojiOf(hw.weatherCode, isDaytimeHour(hour))
  return (
    /* [design 差異] 8080 (`js/charts.js:953`) の強調は
       `scale(1.5)` + `drop-shadow` のグローだが、 2026-09-18 の指摘は
       「少し大きくし、 背景色を入れて」 なので拡大は控えめ (1.25) にして
       背景色で見せる。 8080 側も追随させる。
       `transform` で拡大するのはレイアウトを動かさないため
       (セルは `translateX(-50%)` で中心合わせしているので、 padding は
       常時入れて左右対称に広げ、 背景の有無だけを切り替える)。 */
    <Text
      fontSize="lg"
      borderRadius="full"
      whiteSpace="nowrap"
      backgroundColor={isActive ? 'Surface.accentPrimaryLightLow' : undefined}
      paddingX={1}
      style={{
        transform: isActive ? 'scale(1.25)' : undefined,
        transition: 'transform 0.08s, background-color 0.08s',
      }}
    >
      {emoji !== '' ? emoji : telopLabelOf(hw.weatherCode)}
    </Text>
  )
}

/**
 * 延長領域の実績マーク。本体と同じく中心を point 位置に置く。延長部はチャートの
 * hover 対象外なので強調しない。
 */
const ExtensionMark = ({
  leftPct,
  ...markProps
}: WeatherMarkProps & { leftPct: number }): React.ReactElement => (
  <Flex
    position="absolute"
    top={0}
    alignItems="center"
    flexDirection="column"
    style={{ left: `${leftPct}%`, transform: 'translateX(-50%)' }}
  >
    <WeatherMark {...markProps} />
  </Flex>
)

const WeatherStrip = ({
  data,
  showTime,
  rowLabel,
  ariaLabel,
  extension,
  rightSpacerWidth,
  leftSpacerWidth,
  noDataTitle,
  labelColor = 'Text.lowEmphasis',
  activeHour,
}: WeatherStripProps): React.ReactElement => {
  // data が undefined / hourlyWeather 空でも 24 セル帯自体は描画し、各セルを
  // 「—」にする (D5)。以前は早期 return で行ごと消していたが、AC-DFC-012 の
  // 「未到来・欠測とも『—』」を満たすには帯を出したうえでセルを欠測表示に
  // する必要がある。
  const byHour = toByHour(data?.hourlyWeather)
  const preByHour = toByHour(extension?.pre)
  const postByHour = toByHour(extension?.post)

  return (
    <Flex
      // 外枠 Box の border (1px 双方、border-box) がこの行の content 幅を
      // ForecastChart の外枠 div (border なし) より 2px 狭めるため、
      // 負の margin で相殺し外枠と同じ幅を基準に整列する。
      marginTop={showTime ? 2 : 1}
      aria-label={ariaLabel}
      marginX="-1px"
    >
      <Box
        alignItems="center"
        justifyContent="flex-end"
        flexShrink={0}
        display="flex"
        width={`${leftSpacerWidth ?? LEFT_SPACER_WIDTH}px`}
        paddingRight={2}
      >
        {/* design `.weather-row-labels` = 12px/600 (`demand/forecast-today.html`)。
            実装は 10px/400 でサイズも太さもずれていた (デザイナー指摘 2026-08-28
            「フォントが小さすぎて視認性が悪い」)。 canonical role の captionStrong
            (sm/semibold = 12px/600) がちょうど同値なので token 新設は不要。
            帯の高さは下の非表示スペーサー (lg = 24px) が決めており、 12px では
            届かないため高さ・チャートとの縦位置は不変。 左枠 48px - padding 8px
            = 40px に対し 2 文字 24px で折返しも起きない。 */}
        <Text textStyle="captionStrong" color={labelColor} whiteSpace="nowrap">
          {rowLabel}
        </Text>
      </Box>
      {/* 前日 23:00〜0:00 の延長領域。前日 23 時台のマークは領域左端 (= 23:00 の点)。 */}
      <Box
        position="relative"
        minWidth={0}
        style={{ flex: `${LEFT_EXTENSION_KOMAS} ${LEFT_EXTENSION_KOMAS} 0%` }}
      >
        {extension !== undefined &&
          PRE_EXTENSION_HOURS.map((hour) => (
            <ExtensionMark
              key={hour}
              hour={hour}
              hw={preByHour.get(hour)}
              leftPct={
                ((LEFT_EXTENSION_KOMAS -
                  (HOURS_PER_DAY - hour) * KOMAS_PER_HOUR) /
                  LEFT_EXTENSION_KOMAS) *
                100
              }
              noDataTitle={noDataTitle}
            />
          ))}
      </Box>
      {/* 24 マークを絶対配置で point-scale 位置に置く (先頭マーク=領域左端、
          末尾マーク=領域右端、23 等間隔)。space-between だとマーク自身の
          自然幅の分だけ中心が内側にずれる (実測で確認済み) ため、
          `left: <fraction>%` + `translateX(-50%)` でマークの中心そのものを
          point 位置に固定する (本ファイル冒頭 JSDoc 参照)。 */}
      {/* `containerType: inline-size` はこの領域幅を基準にした container query の
          ためのもの (下の時刻ラベル間引き)。 幅は flex が決めるので inline-size
          containment を入れても配置は変わらない。 */}
      <Box
        position="relative"
        minWidth={0}
        containerType="inline-size"
        style={{
          flex: `${HOUR_MARK_SPAN_KOMAS} ${HOUR_MARK_SPAN_KOMAS} 0%`,
        }}
      >
        {Array.from({ length: HOURS_PER_DAY }, (_, hour) => {
          const hw = byHour.get(hour)
          const leftPct = (hour / (HOURS_PER_DAY - 1)) * 100
          return (
            <Flex
              key={hour}
              position="absolute"
              top={0}
              alignItems="center"
              flexDirection="column"
              gap={0}
              style={{ left: `${leftPct}%`, transform: 'translateX(-50%)' }}
            >
              {showTime && (
                <Text
                  color="Text.lowEmphasis"
                  fontSize="xs"
                  whiteSpace="nowrap"
                  css={
                    hour % HOUR_LABEL_THIN_STEP === 0
                      ? undefined
                      : HOUR_LABEL_THIN_CSS
                  }
                >
                  {String(hour).padStart(2, '0')}
                </Text>
              )}
              <WeatherMark
                hour={hour}
                hw={hw}
                isActive={hour === activeHour}
                noDataTitle={noDataTitle}
              />
            </Flex>
          )
        })}
        {/* 絶対配置の子要素は高さに寄与しないため、可視要素と同じ行構成
            (aria-hidden + 非衝突文字列) で親の高さを自然に確保する。
            実データと同じ文字列 (例: '00') だと getByText の一意性が
            崩れるため、テストの検索対象にならない NBSP を使う。 */}
        {showTime ? (
          <Flex
            alignItems="center"
            flexDirection="column"
            gap={0}
            visibility="hidden"
            aria-hidden="true"
          >
            <Text fontSize="xs">{'\u00A0\u00A0'}</Text>
            <Text fontSize="lg">{'\u00A0'}</Text>
          </Flex>
        ) : (
          <Flex
            alignItems="center"
            flexDirection="column"
            gap={0}
            visibility="hidden"
            aria-hidden="true"
          >
            <Text fontSize="lg">{'\u00A0'}</Text>
          </Flex>
        )}
      </Box>
      {/* 対象日 23:00〜翌日 3:00 の延長領域。翌日 h 時台のマークは (h+1) 時間ぶん右。 */}
      <Box
        position="relative"
        minWidth={0}
        style={{ flex: `${RIGHT_EXTENSION_KOMAS} ${RIGHT_EXTENSION_KOMAS} 0%` }}
      >
        {extension !== undefined &&
          POST_EXTENSION_HOURS.map((hour) => (
            <ExtensionMark
              key={hour}
              hour={hour}
              hw={postByHour.get(hour)}
              leftPct={
                (((hour + 1) * KOMAS_PER_HOUR) / RIGHT_EXTENSION_KOMAS) * 100
              }
              noDataTitle={noDataTitle}
            />
          ))}
      </Box>
      <Box flexShrink={0} width={`${rightSpacerWidth}px`} />
    </Flex>
  )
}

type DailyWeatherRowProps = {
  /**
   * 実績行 (AMeDAS 観測) は backend が当日・過去日のみ供給するため、翌々日
   * 以降の断面では常に undefined / `hourlyWeather` 空で渡る (D5 + D7)。
   * その場合も行自体は描画し、代表 1 セルを「—」にする。
   */
  weather?: ForecastWeatherBar
  ariaLabel: string
  noDataTitle: string
  /**
   * 行ラベル (「予測」「実績」)。 予測行は design 準拠でラベルを出さない
   * (既存挙動、`undefined` のまま)。 実績行は D7 で予測行の下に並ぶため、
   * WeatherStrip の実績行 (`labelColor="Text.positiveHigh"`) と揃えて
   * 区別できるようにする。
   */
  rowLabel?: string
  labelColor?: string
  /**
   * 出典 pill (「日別予報」)。 予測の**粒度**を示す badge なので、実績
   * (観測値) 行では意味を持たない。実績行は常に false で渡す。
   */
  showBadge?: boolean
}

/**
 * 日別 1 個の天気アイコン行 (AC-WD-008a)。
 *
 * 翌々日 (D+2) 以降は JMA が 3 時間粒度の天気を発表しておらず、 backend は
 * 日別 1 値を hourlyWeather の 1 件 (hour=0) で返す。 日別 1 個のアイコンとして
 * 出す (時間帯別に並べると実データに無い粒度を作ってしまう)。
 *
 * 代表値は **正午 (12 時) のブロック**があればそれを、 無ければ先頭の 1 件を採る
 * (design `forecast-today.html` の `_daySlots[4]` と同じ。 日別の源は 1 件のみ
 * なので通常は先頭の 1 件)。
 *
 * 出典 pill は design の `.weather-source-badge` 相当。 ヘッダ行のプロバイダ
 * バッジ (「JMA 公開予報」) とは別物で、 **粒度**が日別であることを示す。
 *
 * 実績行 (D7): backend は翌々日以降の実績天気を供給しないため代表値が
 * 常に存在しない。 その場合も行自体は描画し (D5)、 予報行と同じ日別 1 セル
 * 粒度で「—」を出す (24 セルの帯にすると予報行との粒度が割れる)。
 */
const DailyWeatherRow = ({
  weather,
  ariaLabel,
  noDataTitle,
  rowLabel,
  labelColor = 'Text.lowEmphasis',
  showBadge = true,
}: DailyWeatherRowProps): React.ReactElement => {
  const { t } = useTranslation()
  const byHour = new Map(
    (weather?.hourlyWeather ?? []).map((hw) => [hw.hour, hw]),
  )
  const representative = byHour.get(NOON_HOUR) ?? weather?.hourlyWeather[0]
  // 日別 1 個の代表アイコンは常に昼扱い (日別値に昼夜の別が無い)。
  const representativeEmoji =
    representative === undefined
      ? ''
      : telopEmojiOf(representative.weatherCode, true)
  const representativeLabel =
    representative === undefined ? '' : telopLabelOf(representative.weatherCode)

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      gap={4}
      aria-label={ariaLabel}
      paddingX={3}
      paddingY={2}
      role="group"
    >
      {rowLabel !== undefined && (
        <Text textStyle="captionStrong" color={labelColor} whiteSpace="nowrap">
          {rowLabel}
        </Text>
      )}
      {representative !== undefined ? (
        <>
          <HintTooltip content={representativeLabel}>
            <Text fontSize="2xl" lineHeight="1">
              {representativeEmoji !== ''
                ? representativeEmoji
                : representativeLabel}
            </Text>
          </HintTooltip>
          {showBadge && (
            <HintTooltip
              content={t(
                'page:demandForecastDaily.weather.dailyForecastTooltip',
              )}
            >
              {/* 粒度のラベルであって状態ではないので neutral (design の pill は
                  info 色だが、 色から status を逆算しない)。 */}
              <Badge status="neutral">
                {t('page:demandForecastDaily.weather.dailyForecastBadge')}
              </Badge>
            </HintTooltip>
          )}
        </>
      ) : (
        // 代表値が無い (D7: 実績が翌々日以降に届かない、または予測データ自体が
        // 空) 場合は「—」1 セルにする (AC-DFC-012「未到来・欠測とも『—』」)。
        <HintTooltip content={noDataTitle}>
          <Text
            color="Text.lowEmphasis"
            fontSize="2xl"
            lineHeight="1"
            title={noDataTitle}
          >
            —
          </Text>
        </HintTooltip>
      )}
    </Flex>
  )
}

/** データの無いセルの「—」。 title が無い時は tooltip を付けない。 */
const NoDataMark = ({ title }: { title?: string }): React.ReactElement => {
  const mark = (
    <Text
      color="Text.lowEmphasis"
      fontSize="lg"
      whiteSpace="nowrap"
      title={title}
    >
      —
    </Text>
  )
  if (title === undefined) return mark
  return <HintTooltip content={title}>{mark}</HintTooltip>
}

/** 最高/最低気温の表示。 値が無い時は「—」(AC-DFC-020)。 */
function formatTemperature(value: number | undefined): string {
  return value === undefined ? '—' : `${value.toFixed(1)}℃`
}

export const WeatherBar = ({
  weather,
  actualWeather,
  weatherStatus,
  isTargetDayWeatherMissing = false,
  isPastTargetDate = false,
  preActualHourlyWeather,
  postActualHourlyWeather,
  showRightAxis = false,
  leftAxisWidth,
  isDailyGranularity = false,
  activeHour,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()

  // AC-WD-007a: 翌日断面で対象日の時系列予報 (VPFD) が無い間 (05 時発表前・取込
  // 遅延・取得失敗) は、backend が府県・週間の日別 1 値を 1 件で返す。取得元
  // (`weatherSource`) が日別粒度の源なら D+2 以降と同じ日別 1 個の表示にする。
  // 確定済み表示は確定時に記録した取得元で切り替わる。取得元が不明
  // (UNSPECIFIED: 取得元を記録する前の snapshot) は時間帯別のまま (Slice 70)。
  const isForecastDaily =
    isDailyGranularity || isDailyWeatherSource(weather?.weatherSource)

  // 「実績データが実際にある (backend が観測値を返した)」判定。 2 つの早期
  // return (バー本体を出さない条件) にだけ使う。 「実績行を描くか」の判定
  // ではない (D5: バー本体が描画される時は実績行を常に描き、データが無ければ
  // 「—」で埋める。 早期 return と行の描画有無を混同すると、翌日以降に
  // 実績データが無いだけで行ごと消えてしまう)。 延長部の実績天気も含める
  // (対象日が明日で予報が無い・取得失敗でも、前端 = 今日 23 時台の実績は出す。
  // AC-DFD-006a-2)。 日別粒度の実績行は延長セルを持たないので数えない。
  const hasExtensionActualData =
    !isDailyGranularity &&
    ((preActualHourlyWeather?.length ?? 0) > 0 ||
      (postActualHourlyWeather?.length ?? 0) > 0)
  const hasActualData =
    (actualWeather !== undefined && actualWeather.hourlyWeather.length > 0) ||
    hasExtensionActualData

  // 予報・実績共に無く、取得失敗でもない場合はなにも表示しない
  if (
    weather === undefined &&
    !hasActualData &&
    weatherStatus !== AuxiliaryDataStatus.FETCH_FAILED
  ) {
    return null
  }

  // 予報取得失敗かつ実績も無い場合は、従来通りエラー Alert のみを返す
  if (weatherStatus === AuxiliaryDataStatus.FETCH_FAILED && !hasActualData) {
    return (
      <CompactAlert
        description={t('page:demandForecastDaily.weather.fetchFailed')}
        // TODO: P-1 PdM文言確定
        status="error"
        title={t('page:demandForecastDaily.weather.label')}
      />
    )
  }

  const leftSpacerWidth =
    (leftAxisWidth ?? CHART_LEFT_AXIS_WIDTH) + CHART_MARGIN.left
  const rightSpacerWidth =
    CHART_MARGIN.right + (showRightAxis ? CHART_RIGHT_AXIS_WIDTH : 0)
  const noDataTitle = t('page:demandForecastDaily.weather.noDataTitle')
  // 実績行の欠測理由は予報と別 (未到来 / 天気非配信地点 / 観測の欠測。
  // AC-WD-015)。 予報行の「予測は3時間ごと」を実績セルに出すと誤った説明に
  // なるため文言を分ける (記号は D6 のとおり「—」で共通)。
  const noDataTitleActual = t(
    'page:demandForecastDaily.weather.noDataTitleActual',
  )
  // AC-DFC-020: 過去日で予報のデータが無い時は、予報行と最高/最低気温を「—」で
  // 描く (実績行の「—」と同じ扱い。 AC-DFC-012)。 判定は models/targetDayWeather.ts。
  const showForecastPlaceholders =
    forecastRowMode({
      weather,
      isTargetDayWeatherMissing,
      isPastTargetDate,
    }) === 'placeholder'
  // 予報行は、予報が 1 件も無い未来の断面 (05 時発表前の翌日断面等) では行ごと
  // 出さない。常時描画するのは実績行と、過去日の予報行だけ。
  const hasForecastRow =
    (weather?.hourlyWeather.length ?? 0) > 0 || showForecastPlaceholders

  // AC-DFC-008d: 気象情報バーには発表時刻 (forecastedAt) を表示する。受信・取込
  // 時刻 (receivedAt) はデータ鮮度監視用の内部データであり画面には表示しない
  // (AC-WD-005b、issue #6934 / #6344)。発表時刻は JMA の発表スケジュール
  // (JST 05/11/17 時等) に基づく業務概念なので、端末ローカル TZ ではなく
  // JST 壁時計で表示する。
  const forecastedAt =
    weather?.forecastedAt !== undefined
      ? formatJstDateTimeFull(timestampDate(weather.forecastedAt))
      : undefined

  const maxTemperature =
    weather !== undefined ? decimalToNumber(weather.maxTemperature) : undefined
  const minTemperature =
    weather !== undefined ? decimalToNumber(weather.minTemperature) : undefined
  const hasTemperature =
    maxTemperature !== undefined || minTemperature !== undefined

  return (
    <Box
      background="Surface.secondary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      // design `.weather-strip` は `--radius-base` (4px)。 DS にも base(4px) が
      // あるので raw 値を書かずトークンで同値にする (デザイナー判断 2026-08-28)。
      borderRadius="base"
      paddingY={2}
    >
      {weatherStatus === AuxiliaryDataStatus.FETCH_FAILED && (
        <CompactAlert
          description={t('page:demandForecastDaily.weather.fetchFailed')}
          // TODO: P-1 PdM文言確定
          status="error"
          title={t('page:demandForecastDaily.weather.label')}
        />
      )}

      {weather !== undefined &&
        weatherStatus !== AuxiliaryDataStatus.FETCH_FAILED && (
          <>
            {/* サマリ行の書体は design の同構成バー
                (`demand/forecast-history.html` の `.dfh-weather-bar`) に合わせる。
                あちらは 気象予報元 / 最終取得 / 最高・最低 という同じ中身を
                `font-size: var(--font-size-md)` (= 14px) で組み、 ラベルと気温は 600、
                取得日時だけ normal + mid-emphasis にしている。
                旧実装は 10px (xs/600) で、 その根拠として JSDoc に「design の気象
                chrome に近似一致」 と書かれていたが、 追跡すると値は 2026-06-17 の
                初版が根拠記録なく置いたもので、 コメントは 25 日後の lint 対応時に
                後付けされたものだった。 参照先とされた 8080 側で xs/600 なのは
                取得元 badge のみで、 それは実装では DS Badge として別に描いている。
                (デザイナー指摘 2026-08-28「14px に見えない」)。 */}
            <Flex alignItems="center" flexWrap="wrap" gap={3} paddingX={3}>
              <Text textStyle="label" color="Text.lowEmphasis">
                {t('page:demandForecastDaily.weather.label')}
              </Text>
              {forecastedAt !== undefined && (
                <Text textStyle="body" color="Text.lowEmphasis">
                  {t('page:demandForecastDaily.weather.forecastedAt', {
                    time: forecastedAt,
                  })}
                </Text>
              )}
              {/* 「最高」「最低」 は色分けしない (design `demand/forecast-today.html`
                  の `.weather-daily-temps` は 1 色。 デザイナー判断 2026-08-28)。
                  以前は 最高=Text.negativeHigh / 最低=Text.accentPrimary で塗り分けて
                  いたが、 accentPrimary はリンク色と同値でクリックできる要素と
                  誤認されうる (design/CLAUDE.md 「注釈・補足に青を使わない」)。 */}
              {/* AC-DFC-013: 気温・天気が予測・実績とも無い時は、 最高/最低気温の
                  位置に案内を出す (取得元 badge は残す)。 配置はデザイナー判断で、
                  design に該当画面が無いため気温の位置に置いた。 */}
              {isTargetDayWeatherMissing && (
                <Text textStyle="body" color="Text.lowEmphasis">
                  {t('page:demandForecastDaily.weather.noWeatherData')}
                </Text>
              )}
              {(hasTemperature || showForecastPlaceholders) && (
                <HStack gap={2}>
                  {(maxTemperature !== undefined ||
                    showForecastPlaceholders) && (
                    <Text textStyle="label">
                      {t('page:demandForecastDaily.weather.maxTemp')}{' '}
                      <Box as="span" color="Text.highEmphasis">
                        {formatTemperature(maxTemperature)}
                      </Box>
                    </Text>
                  )}
                  {(minTemperature !== undefined ||
                    showForecastPlaceholders) && (
                    <Text textStyle="label">
                      {t('page:demandForecastDaily.weather.minTemp')}{' '}
                      <Box as="span" color="Text.highEmphasis">
                        {formatTemperature(minTemperature)}
                      </Box>
                    </Text>
                  )}
                </HStack>
              )}
              {/* 取得元 badge (design L5380-5381 の末尾 badge 相当)。整列行と分離した
                理由は本ファイル冒頭 JSDoc 参照。 */}
              <Badge flexShrink={0} marginLeft="auto" status="neutral">
                {weather.dataSource}
              </Badge>
            </Flex>

            {/* 予測行。 翌々日以降 (AC-WD-008a) は 24 時間帯の帯をやめて
                日別 1 個のアイコンにする。 design `demand/forecast-today.html` の
                `.weather-strip-daily` (中央寄せ / 2xl アイコン / 出典 pill) 準拠。
                最高・最低気温は上のヘッダ行が既に出しているのでここでは重ねない
                (design はヘッダを持たないぶん帯の中に温度を置いている)。 */}
            {hasForecastRow &&
              (isForecastDaily ? (
                <DailyWeatherRow
                  ariaLabel={t('page:demandForecastDaily.weather.dailyWeather')}
                  noDataTitle={noDataTitle}
                  weather={weather}
                />
              ) : (
                <WeatherStrip
                  activeHour={activeHour}
                  ariaLabel={t(
                    'page:demandForecastDaily.weather.hourlyWeather',
                  )}
                  data={weather}
                  leftSpacerWidth={leftSpacerWidth}
                  // 予報のデータが無い過去日は「—」だけを出し、 案内 (tooltip) は
                  // 付けない (AC-DFC-020、 design forecast-today.html の wx-nodata)。
                  noDataTitle={
                    showForecastPlaceholders ? undefined : noDataTitle
                  }
                  rightSpacerWidth={rightSpacerWidth}
                  rowLabel={t('page:demandForecastDaily.weather.forecastLabel')}
                  showTime
                />
              ))}
          </>
        )}

      {/* 実績行 (AMeDAS 観測)。バー本体が描画される時 (ここに到達する時) は
          対象日 (当日 / 過去日 / 翌日 / 翌々日以降) によらず常に描画する
          (D5)。backend が実績を供給しない断面 (翌日以降) では actualWeather
          が undefined / hourlyWeather 空で渡るが、その場合も行自体は出し
          未到来・欠測のコマを「—」にする (AC-DFC-012)。 ただし対象日の
          気温・天気が予測・実績とも無い時は、 上の案内に置き換えて行を出さない
          (AC-DFC-013。「—」 だけの行が並ぶと、 データが無いのか未到来なのかが
          読めないため)。 延長部に実績がある時は案内を残したまま実績行を出す
          (AC-DFD-006a-2: 実績が存在する時刻の実績天気は表示する)。 */}
      {(!isTargetDayWeatherMissing || hasExtensionActualData) &&
        (isDailyGranularity ? (
          <DailyWeatherRow
            ariaLabel={t('page:demandForecastDaily.weather.dailyWeatherActual')}
            labelColor="Text.positiveHigh"
            noDataTitle={noDataTitleActual}
            rowLabel={t('page:demandForecastDaily.weather.actualLabel')}
            showBadge={false}
            weather={actualWeather}
          />
        ) : (
          <WeatherStrip
            activeHour={activeHour}
            ariaLabel={t(
              'page:demandForecastDaily.weather.hourlyWeatherActual',
            )}
            data={actualWeather}
            extension={{
              pre: preActualHourlyWeather,
              post: postActualHourlyWeather,
            }}
            labelColor="Text.positiveHigh"
            leftSpacerWidth={leftSpacerWidth}
            noDataTitle={noDataTitleActual}
            rightSpacerWidth={rightSpacerWidth}
            rowLabel={t('page:demandForecastDaily.weather.actualLabel')}
            showTime={false}
          />
        ))}
    </Box>
  )
}
